import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, ShoppingBag, CreditCard, Banknote, Wine, Coffee, Milk, Apple, Ticket, Wifi } from 'lucide-react';
import DealsCarousel from '@/components/DealsCarousel';

const Home = () => {
  const services = [
    { icon: CreditCard, title: 'ATM Available', desc: '24/7 cash access' },
    { icon: Banknote, title: 'Bill Payment', desc: 'Pay bills easily' },
    { icon: Ticket, title: 'Lottery', desc: 'Try your luck' },
    { icon: Wifi, title: 'EBT Accepted', desc: 'SNAP benefits welcome' },
  ];

  const products = [
    {
      title: 'Cold Beer & Beverages',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/7mql3ceq_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%281%29.jpeg',
      span: 'col-span-2 row-span-2',
    },
    {
      title: 'Fountain Drinks & Coffee',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/upjqcsds_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%283%29.jpeg',
      span: 'col-span-1 row-span-1',
    },
    {
      title: 'Snacks & Chocolates',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/mkasgc67_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM.jpeg',
      span: 'col-span-1 row-span-2',
    },
    {
      title: 'Wine & More',
      image: 'https://customer-assets.emergentagent.com/job_website-build-11/artifacts/6m4ot12p_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%284%29.jpeg',
      span: 'col-span-2 row-span-1',
    },
  ];

  return (
    <div className="grain-bg" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_website-build-11/artifacts/7mql3ceq_WhatsApp%20Image%202026-02-12%20at%204.50.39%20PM%20%281%29.jpeg"
            alt="Inside Shah Market store"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A4D2E]/95 via-[#1A4D2E]/80 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="open-badge mb-6 animate-fade-in-up">
              Open Now
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Your Friendly<br />
              <span className="text-[#E07A5F]">Neighborhood</span><br />
              Convenience Store
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 animate-fade-in-up animation-delay-200">
              Fresh groceries, cold drinks, everyday essentials, and a warm welcome. Shah Market has been serving the Yettem community with care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-300">
              <Link
                to="/location"
                className="btn-secondary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 text-lg"
                data-testid="hero-cta-location"
              >
                <MapPin size={20} />
                Stop By Today
              </Link>
              <a
                href="tel:5094057493"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 text-lg hover:bg-white/20 transition-all"
                data-testid="hero-cta-call"
              >
                <Phone size={20} />
                Call Us Now
              </a>
            </div>
          </div>
        </div>

        {/* Floating Info Card */}
        <div className="hidden lg:block absolute right-8 xl:right-16 top-1/2 -translate-y-1/2 z-10">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-72 animate-slide-in-right">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#F4F1DE] rounded-xl flex items-center justify-center">
                <Clock size={24} className="text-[#1A4D2E]" />
              </div>
              <div>
                <p className="font-bold text-[#3D405B]\">Today's Hours</p>
                <p className="text-[#5F6C7B]">6:00 AM - 10:00 PM</p>
              </div>
            </div>
            <div className="border-t border-[#F4F1DE] pt-4">
              <p className="flex items-center gap-2 text-sm text-[#5F6C7B] mb-2">
                <MapPin size={16} className="text-[#E07A5F]" />
                14346 Avenue 384, Yettem
              </p>
              <p className="flex items-center gap-2 text-sm text-[#5F6C7B]">
                <Phone size={16} className="text-[#E07A5F]" />
                (509) 405-7493
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B] mb-4">
              More Than Just a Store
            </h2>
            <p className="text-[#5F6C7B] text-lg max-w-2xl mx-auto">
              We offer convenient services to make your life easier
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card"
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon">
                  <service.icon size={28} />
                </div>
                <h3 className="font-bold text-[#3D405B] mb-1">{service.title}</h3>
                <p className="text-sm text-[#5F6C7B]">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Showcase */}
      <section className="py-16 md:py-24" data-testid="products-preview">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#3D405B] mb-4">
                What We Offer
              </h2>
              <p className="text-[#5F6C7B] text-lg max-w-xl">
                From fresh produce to your favorite snacks, we've got everything you need.
              </p>
            </div>
            <Link
              to="/products"
              className="btn-primary px-6 py-3 rounded-xl font-medium inline-flex items-center gap-2"
              data-testid="view-all-products"
            >
              <ShoppingBag size={18} />
              View All Products
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {products.map((product, index) => (
              <div
                key={index}
                className={`product-card ${product.span} min-h-[200px] md:min-h-[250px]`}
                data-testid={`product-card-${index}`}
              >
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
                <div className="product-card-overlay">
                  <h3 className="text-white font-bold text-lg md:text-xl">{product.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1A4D2E]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Come Visit Us Today!
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            We're conveniently located in the heart of Yettem. Stop by for friendly service and all your everyday needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/location"
              className="btn-secondary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 text-lg"
              data-testid="cta-find-location"
            >
              <MapPin size={20} />
              Find Our Location
            </Link>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 text-lg hover:bg-white/20 transition-all"
              data-testid="cta-contact"
            >
              <Phone size={20} />
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
